import { repeatUntil } from "shared/lib/repeatUntil"
import { useEvent } from "shared/lib/useEvent"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { ProjectImportStatus } from "shared/types/ProjectImport"
import { ProjectImport } from "shared/types/__generated__/graphql"

import { useProjectImportLazyQuery } from "entities/projects/graphql/import-mutations.generated"

function isImportFinished(importStatus: ProjectImport): boolean {
  return importStatus.status === ProjectImportStatus.FINISHED
}

export function useImportStatus(options?: {
  interval?: number
  timeout?: number
}) {
  const [fetchImportStatus] = useProjectImportLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })

  const handleImportStatus = useEvent(
    <T>({
      importId,
      onComplete
    }: {
      importId: string
      onComplete: (importStatus: ProjectImport) => Promise<T>
    }) => {
      return repeatUntil(
        () =>
          fetchImportStatus({ variables: { id: importId } }).then(
            result =>
              result.data?.projectImport ?? {
                id: importId,
                __typename: "ProjectImport" as const,
                status: undefined,
                error: undefined
              }
          ),
        isImportFinished,
        onComplete,
        {
          initialInterval: options?.interval,
          timeout: options?.timeout,
          failurePredicateFn: p =>
            p.status === ProjectImportStatus.ERROR
              ? p.error || "Import failed"
              : null
        }
      ).promise
    }
  )

  return handleImportStatus
}
